import React, { useState, useEffect } from 'react';
import {
  Box, Chip, Typography, IconButton, Menu, MenuItem,
  TextField, Dialog, DialogActions, DialogContent, 
  DialogTitle, Button, Popover, CircularProgress,
  List, ListItem, ListItemText, ListItemSecondaryAction,
  Divider, useTheme, alpha
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LabelIcon from '@mui/icons-material/Label';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { TwitterPicker } from 'react-color';

import { addDocumentTags, removeDocumentTag } from '../utils/documentService';

/**
 * Component for managing document tags
 * @param {Object} props
 * @param {Array} props.tags - Current tags of the document
 * @param {string} props.documentId - Document ID
 * @param {Function} props.onTagsChange - Callback when tags change
 */
const DocumentTags = ({ tags = [], documentId, onTagsChange }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  
  // New tag state
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#F5821F'); // Default to brand orange
  
  // Predefined colors for tags
  const tagColors = [
    '#F5821F', // Brand orange
    '#2196F3', // Blue
    '#4CAF50', // Green
    '#F44336', // Red
    '#9C27B0', // Purple
    '#FF9800', // Orange
    '#607D8B', // Blue Grey
    '#795548', // Brown
  ];
  
  // Handle menu open
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Open add tag dialog
  const handleAddTagClick = () => {
    handleMenuClose();
    setDialogOpen(true);
    setNewTagName('');
    setNewTagColor('#F5821F');
  };
  
  // Open edit tag dialog
  const handleEditTagClick = (tag) => {
    setSelectedTag(tag);
    setNewTagName(tag.name);
    setNewTagColor(tag.color || '#F5821F');
    setDialogOpen(true);
  };
  
  // Close dialog
  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTag(null);
  };
  
  // Open color picker
  const handleColorPickerOpen = (event) => {
    setColorAnchorEl(event.currentTarget);
    setColorPickerOpen(true);
  };
  
  // Close color picker
  const handleColorPickerClose = () => {
    setColorPickerOpen(false);
    setColorAnchorEl(null);
  };
  
  // Change color
  const handleColorChange = (color) => {
    setNewTagColor(color.hex);
  };
  
  // Add a new tag
  const handleAddTag = async () => {
    if (!newTagName.trim()) return;
    
    setLoading(true);
    try {
      // Send tag object with name and color
      const tagData = {
        name: newTagName.trim(),
        color: newTagColor
      };
      
      const result = await addDocumentTags(documentId, [tagData]);
      if (result.success) {
        // Call the onTagsChange callback with updated tags
        if (onTagsChange) {
          onTagsChange(result.tags);
        }
        // Clear the form and close dialog on success
        setNewTagName('');
        setNewTagColor('#F5821F');
        handleDialogClose();
      } else {
        console.error('Error adding tag:', result.error);
        alert('Failed to add tag: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      alert('Failed to add tag: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Remove a tag
  const handleRemoveTag = async (tagName) => {
    setLoading(true);
    try {
      const result = await removeDocumentTag(documentId, tagName);
      if (result.success) {
        // Call the onTagsChange callback with updated tags
        if (onTagsChange) {
          onTagsChange(result.tags);
        }
      }
    } catch (error) {
      console.error('Error removing tag:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
          <LabelIcon fontSize="small" sx={{ mr: 0.5 }} /> Tags
        </Typography>
        
        <IconButton 
          size="small" 
          onClick={handleMenuOpen}
          sx={{ 
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
            }
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {tags.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}>
            No tags added yet
          </Typography>
        ) : (
          tags.map((tag, index) => (
            <Chip
              key={index}
              label={tag.name}
              size="small"
              sx={{
                backgroundColor: tag.color || theme.palette.primary.main,
                color: '#fff',
                '&:hover': {
                  backgroundColor: alpha(tag.color || theme.palette.primary.main, 0.8)
                }
              }}
              onDelete={() => handleRemoveTag(tag.name)}
              onClick={() => handleEditTagClick(tag)}
            />
          ))
        )}
        
        {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
      </Box>
      
      {/* Tag Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleAddTagClick}>
          <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add New Tag
        </MenuItem>
      </Menu>
      
      {/* Add/Edit Tag Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          {selectedTag ? 'Edit Tag' : 'Add New Tag'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              label="Tag Name"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
            />
            
            <Typography variant="subtitle2" gutterBottom>
              Tag Color
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: 1, 
                  backgroundColor: newTagColor,
                  cursor: 'pointer',
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  mr: 1
                }}
                onClick={handleColorPickerOpen}
              />
              
              <Button
                variant="outlined"
                size="small"
                onClick={handleColorPickerOpen}
              >
                Change Color
              </Button>
            </Box>
            
            <Popover
              open={colorPickerOpen}
              anchorEl={colorAnchorEl}
              onClose={handleColorPickerClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <TwitterPicker 
                color={newTagColor}
                onChange={handleColorChange}
                colors={tagColors}
              />
            </Popover>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleAddTag} 
            color="primary" 
            variant="contained"
            disabled={!newTagName.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {selectedTag ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentTags;